import safeParse from './safe-parse';
import Store from '../redux/store';
import { getText } from '../helpers';
import SPGVariables from './spg-variables';
const spgVariables = SPGVariables();

const getTimestamp = (publishedAt: string): number => {
    const timestamp = Date.parse(publishedAt);
    return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getMonthYearKey = (publishedAt: string): string => {
    const date = new Date(publishedAt);
    if (Number.isNaN(date.getTime())) {
        return 'Unknown';
    }

    return date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
    });
};

const formatPublishedAt = (publishedAt: string): string => {
    const date = new Date(publishedAt);
    if (Number.isNaN(date.getTime())) {
        return publishedAt;
    }

    return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });
};

const transformAnnouncementsArchive = (
    announcements: AnnouncementItem[]
): Record<string, AnnouncementItem[]> => {
    const sortedAnnouncements = [...announcements].sort((a, b) => {
        return getTimestamp(b.PublishedAt) - getTimestamp(a.PublishedAt);
    });

    return sortedAnnouncements.reduce((grouped, announcement) => {
        const monthYear = getMonthYearKey(announcement.PublishedAt);
        const formattedAnnouncement = {
            ...announcement,
            PublishedAt: formatPublishedAt(announcement.PublishedAt)
        };

        if (!grouped[monthYear]) {
            grouped[monthYear] = [];
        }

        grouped[monthYear].push(formattedAnnouncement);
        return grouped;
    }, {} as Record<string, AnnouncementItem[]>);
};

const convertUrlToCurrentLanguage = (url: string): string => {
    try {
        const urlObj = new URL(url, window.location.origin);
        const filtersParam = urlObj.searchParams.get('filters');

        if (!filtersParam) return url;

        const parsedFilters = safeParse(filtersParam || '{}');

        if (!parsedFilters || Array.isArray(parsedFilters) || typeof parsedFilters !== 'object') {
            return url;
        }

        const storeOtherLanguageResponses = Store.getState()?.filters?.otherLanguageResponses || {};
        const apiLangCode = getText('langCode') as string;
        const tab = urlObj.searchParams.get('tab');

        if (!storeOtherLanguageResponses || !Object.keys(storeOtherLanguageResponses).length) {
            return url;
        }

        const currentLanguageFilters = storeOtherLanguageResponses[apiLangCode] || {};

        if (!Object.keys(currentLanguageFilters).length) {
            return url;
        }

        // Define which filter keys should be translated
        const translatableFilterKeys = [
            'industry',
            'category',
            'region',
            'history',
            'pointInTime',
            'delivery',
            'queryCategories',
            'solutionCategories',
            'blueprintCategories',
            'collections',
            'bookmark'
        ];

        const getItemsForKey = (langFilters: any, filterKey: string): any[] => {
            if (!langFilters) return [];

            let mappedKey = filterKey;

            if (filterKey === 'category') {
                if (tab === '2') {
                    mappedKey = 'solutionCategories';
                } else if (tab === '3') {
                    mappedKey = 'blueprintCategories';
                } else {
                    mappedKey = 'category';
                }
            }

            if (filterKey === 'collections' || filterKey === 'bookmark') {
                mappedKey = 'bookmark';
            }

            return Array.isArray(langFilters[mappedKey]) ? langFilters[mappedKey] : [];
        };

        const valuesMatch = (value1: any, value2: any): boolean => {
            if (value1 === value2) return true;

            if (value1 == null && value2 == null) return true;

            if (value1 == null || value2 == null) return false;

            const num1 = Number(value1);
            const num2 = Number(value2);

            if (!Number.isNaN(num1) && !Number.isNaN(num2)) {
                return num1 === num2;
            }

            return String(value1).trim() === String(value2).trim();
        };

        const convertedFilters: Record<string, unknown> = {};

        Object.keys(parsedFilters).forEach((filterKey: string) => {
            const filterValue = parsedFilters[filterKey];

            if (!Array.isArray(filterValue)) {
                if (filterValue !== undefined && filterValue !== null) {
                    convertedFilters[filterKey] = filterValue;
                }
                return;
            }

            if (filterValue.length === 0) {
                return;
            }

            // Check if this filter key should be translated
            const shouldTranslate = translatableFilterKeys.includes(filterKey);

            if (!shouldTranslate) {
                // Keep the filter as-is (e.g., collectionId, bookmark, etc.)
                convertedFilters[filterKey] = filterValue;
                return;
            }

            const convertedNames: string[] = [];

            filterValue.forEach((filterName: string) => {
                if (!filterName || typeof filterName !== 'string' || !filterName.trim()) {
                    return;
                }

                const trimmedFilterName = filterName.trim();
                let matchedValue: any = null;

                const valueFound = Object.keys(storeOtherLanguageResponses).some(langCode => {
                    const langFilters = storeOtherLanguageResponses[langCode];
                    const items = getItemsForKey(langFilters, filterKey);

                    const match = items.find((item: any) => {
                        return item && typeof item === 'object' && 'name' in item && 'value' in item && item.name === trimmedFilterName;
                    });

                    if (match) {
                        matchedValue = match.value;
                        return true;
                    }

                    return false;
                });

                if (valueFound) {
                    const currentItems = getItemsForKey(currentLanguageFilters, filterKey);

                    const currentMatch = currentItems.find((item: any) => {
                        return item && typeof item === 'object' && 'value' in item && valuesMatch(item.value, matchedValue);
                    });

                    if (currentMatch && currentMatch.name && typeof currentMatch.name === 'string') {
                        convertedNames.push(currentMatch.name);
                    } else {
                        convertedNames.push(trimmedFilterName);
                    }
                } else {
                    convertedNames.push(trimmedFilterName);
                }
            });

            if (convertedNames.length > 0) {
                convertedFilters[filterKey] = convertedNames;
            }
        });

        const validLangCodes = spgVariables.SUPPORTED_LANGUAGES || ['en', 'zh', 'ja'];
        const pathParts = urlObj.pathname.split('/').filter(part => part);

        if (pathParts.length > 0 && validLangCodes.includes(pathParts[0])) {
            pathParts.shift();
            urlObj.pathname = `/${pathParts.join('/')}`;
        }

        urlObj.searchParams.set('filters', JSON.stringify(convertedFilters));

        return `${urlObj.pathname}${urlObj.search}`;
    } catch (err) {
        return url;
    }
};

export { transformAnnouncementsArchive, convertUrlToCurrentLanguage };
